import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getGameByID } from "../../services/gamesAPI";
import { Button } from "../../components/ui/btns/Button";
import { Modal } from "../../components/modals/Modal";
import { CreateFight } from "../../components/admin/CreateFight";
import { formatDate } from "../../utils/dateAndTime";
import { CreateGame } from "../../components/admin/CreateGame";
import Loader from "../../components/ui/loader";
import DeleteFight from "../../components/modals/DeleteFight";
import { useAuthStore } from "../../store/authStore";
import teamPlaceholder from "../../assets/team-logo-placeholder.svg";

export const Game = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const { gameID } = useParams();
  const [game, setGame] = useState(null);
  const [openCreateFight, setOpenCreateFight] = useState(false);
  const [isOpenModal, setOpenModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFight, setSelectedFight] = useState(null);
  const [isOpenDeleteModal, setOpenDeleteModal] = useState(false);
  const [hasAwayImageError, setHasAwayImageError] = useState(false);
  const [hasHomeImageError, setHasHomeImageError] = useState(false);

  const handleAddFightClick = () => {
    setOpenCreateFight(true);
  };

  const closeAddFight = () => {
    setOpenCreateFight(false);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };
  const fetchGame = async () => {
    setIsLoading(true);
    try {
      let response = await getGameByID(gameID);
      console.log(response);
      if (response.success) {
        setGame(response.data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchGame();
  }, [gameID]); // eslint-disable-line

  useEffect(() => {
    setHasAwayImageError(false);
    setHasHomeImageError(false);
  }, [gameID]);

  const handleFightClick = (fightID) => {
    navigate(`/fight/${fightID}`);
  };

  const sortedFights = game?.Fights?.slice().sort(
    (a, b) =>
      (a.period || 999) - (b.period || 999) ||
      (a.time || "").localeCompare(b.time || "")
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-flyers-black">
        <Loader />
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 sm:p-6 lg:p-8 bg-flyers-black text-white">
      <div className="max-w-6xl mx-auto">
        {game && (
          <>
            {/* Matchup Hero Card */}
            <div className="relative bg-flyers-charcoal border border-flyers-dark-gray rounded-lg p-6 sm:p-8">
              {user?.role === "admin" && (
                <div className="absolute top-4 right-4">
                  <Button onClick={() => setOpenModal(true)}>Update Game</Button>
                </div>
              )}

              <div className="flex flex-col md:flex-row items-center justify-around gap-6 mt-2">
                {/* Away Team */}
                <div className="flex flex-col items-center gap-3">
                  {game.AwayTeamDetails.teamImageFile && !hasAwayImageError ? (
                    <img
                      src={game.AwayTeamDetails.teamImageFile}
                      alt={`${game.AwayTeamDetails.city} ${game.AwayTeamDetails.name} logo`}
                      className="w-20 h-20 sm:w-28 sm:h-28 object-contain rounded-lg border-2 border-flyers-dark-gray p-2"
                      onError={() => setHasAwayImageError(true)}
                    />
                  ) : (
                    <img
                      src={teamPlaceholder}
                      alt="Team logo placeholder"
                      className="w-20 h-20 sm:w-28 sm:h-28 object-contain bg-flyers-black rounded-lg border-2 border-flyers-dark-gray p-2"
                    />
                  )}
                  <div className="text-center">
                    <Link
                      to={`/team/${game.AwayTeamDetails.TeamID}`}
                      className="font-heading text-xl sm:text-2xl font-black uppercase text-flyers-orange hover:text-flyers-orange-light transition-colors"
                    >
                      {game.AwayTeamDetails.name}
                    </Link>
                    <p className="text-flyers-mid-gray text-sm">{game.AwayTeamDetails.city}</p>
                  </div>
                </div>

                {/* VS Divider */}
                <span className="font-heading text-3xl font-black text-flyers-mid-gray">VS</span>

                {/* Home Team */}
                <div className="flex flex-col items-center gap-3">
                  {game.HomeTeamDetails.teamImageFile && !hasHomeImageError ? (
                    <img
                      src={game.HomeTeamDetails.teamImageFile}
                      alt={`${game.HomeTeamDetails.city} ${game.HomeTeamDetails.name} logo`}
                      className="w-20 h-20 sm:w-28 sm:h-28 object-contain rounded-lg border-2 border-flyers-dark-gray p-2"
                      onError={() => setHasHomeImageError(true)}
                    />
                  ) : (
                    <img
                      src={teamPlaceholder}
                      alt="Team logo placeholder"
                      className="w-20 h-20 sm:w-28 sm:h-28 object-contain bg-flyers-black rounded-lg border-2 border-flyers-dark-gray p-2"
                    />
                  )}
                  <div className="text-center">
                    <Link
                      to={`/team/${game.HomeTeamDetails.TeamID}`}
                      className="font-heading text-xl sm:text-2xl font-black uppercase text-flyers-orange hover:text-flyers-orange-light transition-colors"
                    >
                      {game.HomeTeamDetails.name}
                    </Link>
                    <p className="text-flyers-mid-gray text-sm">{game.HomeTeamDetails.city}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Game Details Card */}
            <div className="bg-flyers-charcoal border border-flyers-dark-gray rounded-lg p-6 mt-4">
              <div className="w-16 h-0.5 bg-flyers-orange mb-4 mx-auto" />
              <div className="flex flex-wrap justify-center gap-x-8 gap-y-3">
                <div className="text-center">
                  <span className="text-flyers-mid-gray text-sm font-heading uppercase tracking-wider">Date</span>
                  <p className="text-white font-semibold">{formatDate(game.date)}</p>
                </div>
                <div className="text-center">
                  <span className="text-flyers-mid-gray text-sm font-heading uppercase tracking-wider">Season</span>
                  <p className="text-white font-semibold">{game.SeasonDetails.season}</p>
                </div>
                <div className="text-center">
                  <span className="text-flyers-mid-gray text-sm font-heading uppercase tracking-wider">League</span>
                  <p>
                    <Link
                      to={`/league/${game.LeagueDetails.LeagueID}`}
                      className="text-flyers-orange font-semibold hover:text-flyers-orange-light transition-colors"
                    >
                      {game.LeagueDetails.name}
                    </Link>
                  </p>
                </div>
                {game.gameType && (
                  <div className="text-center">
                    <span className="text-flyers-mid-gray text-sm font-heading uppercase tracking-wider">Game Type</span>
                    <p className="text-white font-semibold">{game.gameType}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Fights Table */}
            <div className="mt-8 border border-flyers-dark-gray rounded-lg overflow-hidden">
              <div className="bg-flyers-charcoal p-4 flex flex-col sm:flex-row justify-between items-center gap-4">
                <h3 className="font-heading text-2xl font-black uppercase tracking-wide text-flyers-orange">
                  Fights
                </h3>
                {user?.role === "admin" && (
                  <Button onClick={handleAddFightClick}>Add Fight</Button>
                )}
              </div>
              <div className="p-4">
                <div className="overflow-x-auto">
                  <table className="w-full bg-flyers-black">
                    <thead>
                      <tr>
                        <th className="bg-flyers-charcoal text-flyers-orange font-heading font-bold uppercase tracking-wider text-sm py-3 px-4 text-start border-b-2 border-flyers-orange">Period</th>
                        <th className="bg-flyers-charcoal text-flyers-orange font-heading font-bold uppercase tracking-wider text-sm py-3 px-4 text-start border-b-2 border-flyers-orange">Time</th>
                        <th className="bg-flyers-charcoal text-flyers-orange font-heading font-bold uppercase tracking-wider text-sm py-3 px-4 text-start border-b-2 border-flyers-orange">Player 1</th>
                        <th className="bg-flyers-charcoal text-flyers-orange font-heading font-bold uppercase tracking-wider text-sm py-3 px-4 text-start border-b-2 border-flyers-orange">Player 2</th>
                        <th className="bg-flyers-charcoal text-flyers-orange font-heading font-bold uppercase tracking-wider text-sm py-3 px-4 text-start border-b-2 border-flyers-orange">Comments</th>
                        <th className="bg-flyers-charcoal text-flyers-orange font-heading font-bold uppercase tracking-wider text-sm py-3 px-4 text-start border-b-2 border-flyers-orange">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedFights?.map((fight) => (
                        <tr className="border-b border-flyers-dark-gray hover:bg-flyers-charcoal transition-colors" key={fight.FightID}>
                          <td className="py-3 px-4 text-sm text-flyers-light-gray">{fight.period || "—"}</td>
                          <td className="py-3 px-4 text-sm text-flyers-light-gray">{fight.time || "—"}</td>
                          <td className="py-3 px-4 text-sm">
                            <Link className="text-flyers-orange hover:text-flyers-orange-light" to={`/player/${fight?.player1}`}>
                              {fight.Player1Details
                                ? `${fight.Player1Details.firstName} ${fight.Player1Details.lastName} (${fight?.stats?.player1Votes} Votes)`
                                : fight.fightType}
                            </Link>
                          </td>
                          <td className="py-3 px-4 text-sm">
                            <Link className="text-flyers-orange hover:text-flyers-orange-light" to={`/player/${fight?.player2}`}>
                              {fight.Player2Details
                                ? `${fight.Player2Details.firstName} ${fight.Player2Details.lastName} (${fight?.stats?.player2Votes} Votes)`
                                : fight.fightType}
                            </Link>
                          </td>
                          <td className="py-3 px-4 text-sm text-flyers-light-gray">{fight?.stats?.totalComments}</td>
                          <td className="py-3 px-4 text-center">
                            <div className="flex gap-3">
                              <Button
                                className="text-sm px-3 py-1"
                                onClick={() => handleFightClick(fight.FightID)}
                              >
                                View
                              </Button>
                              {user?.role === "admin" && (
                                <Button
                                  variant="danger"
                                  className="text-sm px-3 py-1"
                                  onClick={() => {
                                    setSelectedFight(fight.FightID);
                                    setOpenDeleteModal(true);
                                  }}
                                >
                                  Delete
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}

        {openCreateFight && (
          <Modal isOpen={openCreateFight} close={closeAddFight}>
            <CreateFight gameInfo={game} fetchGame={fetchGame} closeModel={closeAddFight} />
          </Modal>
        )}

        {isOpenModal && (
          <Modal isOpen={isOpenModal} close={handleCloseModal}>
            <CreateGame id={gameID} game={game} handleClose={handleCloseModal} fetchGame={fetchGame} />
          </Modal>
        )}

        {isOpenDeleteModal && (
          <DeleteFight
            close={() => { setOpenDeleteModal(false); setSelectedFight(null); }}
            fightID={selectedFight}
            isOpen={isOpenDeleteModal}
            fetchGames={fetchGame}
          />
        )}
      </div>
    </div>
  );
};
